import { Link } from '@tanstack/react-router'
import {
  Calendar,
  ChartGantt,
  ChevronsUpDown,
  Columns3,
  Image,
  List,
  Table2,
  TextCursorInput,
  type LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { View, ViewTypeName } from '@/api/types'
import { byOrder } from '@/shared/lib/order'
import { Button } from '@/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'

/**
 * An icon per view type, exhaustively.
 *
 * `satisfies Record<ViewTypeName, …>` is the point: an eighth view type fails to
 * compile until it has one, rather than rendering as a gap nobody notices.
 */
const VIEW_ICONS = {
  grid: Table2,
  gallery: Image,
  form: TextCursorInput,
  board: Columns3,
  schedule: Calendar,
  gantt: ChartGantt,
  list: List,
} satisfies Record<ViewTypeName, LucideIcon>

/**
 * Which view of the table is on screen, and the way to another.
 *
 * Every entry is a real link carrying `?view=`, not a button that sets state.
 * That is what makes the view part of the address: it can be copied, bookmarked,
 * opened in a new tab and gone back to, and the browser's own history is the
 * undo. A dropdown that mutated a store would give up all four.
 */
export function ViewSwitcher({
  views,
  current,
  tableId,
  databaseId,
}: {
  views: readonly View[]
  current: View | undefined
  tableId: number
  databaseId: number
}) {
  const { t } = useTranslation()
  const ordered = byOrder(views)
  const CurrentIcon = current === undefined ? Table2 : VIEW_ICONS[current.type]

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <CurrentIcon className="size-4 shrink-0 opacity-70" />
          <span className="truncate">{current?.name ?? t('table.noView')}</span>
          <ChevronsUpDown className="size-3.5 shrink-0 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        {ordered.map((view) => {
          const Icon = VIEW_ICONS[view.type]
          return (
            <DropdownMenuItem key={view.id} asChild>
              <Link
                to="/database/$databaseId/table/$tableId"
                params={{ databaseId, tableId }}
                // Only the view: the search term and the open row are both
                // about the rows of the view being left, and carrying either
                // into a different one would be carrying it somewhere it does
                // not mean the same thing.
                search={{ view: view.id }}
                // No `aria-current` here: the router sets it on the link whose
                // destination is where you already are, which is exactly this
                // one. Writing it as well was the first draft, and the mutation
                // pass found it -- removing it changed nothing, because the
                // router had it covered.
                className="flex items-center gap-2"
              >
                <Icon className="size-4 shrink-0 opacity-70" />
                <span className="truncate">{view.name}</span>
              </Link>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
